import { useEffect, useMemo, useState } from "react";

const SPELLING_OVERRIDES = {
  experiance: "experience",
  acheivement: "achievement",
  acheivements: "achievements",
  acheived: "achieved",
  responsiblity: "responsibility",
  responsiblities: "responsibilities",
  comunication: "communication",
  sucess: "success",
  succesful: "successful",
  managment: "management",
  developement: "development",
  develpment: "development",
  enviornment: "environment",
  enviornments: "environments",
  improvment: "improvement",
  improvments: "improvements",
  architechture: "architecture",
  technolgies: "technologies",
  technolgy: "technology",
  profressional: "professional",
  javscript: "javascript",
  javasript: "javascript",
  typesript: "typescript",
  typescriptt: "typescript",
  frontent: "frontend",
  backent: "backend",
  colabrated: "collaborated",
  collabrated: "collaborated",
  acheive: "achieve",
  acheiving: "achieving",
};

const RESUME_VOCABULARY = [
  "agile",
  "analysis",
  "analytics",
  "angular",
  "application",
  "applications",
  "architect",
  "architecture",
  "award",
  "aws",
  "backend",
  "bootstrap",
  "candidate",
  "career",
  "client",
  "cloud",
  "collaborated",
  "communication",
  "compliance",
  "component",
  "components",
  "consultant",
  "content",
  "css3",
  "dashboard",
  "dashboards",
  "database",
  "databases",
  "data",
  "delivered",
  "delivery",
  "deployment",
  "design",
  "designed",
  "developer",
  "development",
  "documentation",
  "education",
  "engineer",
  "enhancement",
  "environment",
  "enterprise",
  "experience",
  "express",
  "figma",
  "frontend",
  "functionality",
  "git",
  "github",
  "html5",
  "implemented",
  "improved",
  "improvement",
  "integrated",
  "integration",
  "interface",
  "interfaces",
  "javascript",
  "jira",
  "leadership",
  "manager",
  "mentorship",
  "mobile",
  "module",
  "modules",
  "mongodb",
  "mysql",
  "nextjs",
  "nodejs",
  "objective",
  "optimization",
  "optimized",
  "performance",
  "platform",
  "problem",
  "process",
  "product",
  "professional",
  "profile",
  "project",
  "projects",
  "python",
  "quality",
  "react",
  "reactjs",
  "react.js",
  "recharts",
  "redux",
  "reporting",
  "responsive",
  "responsibilities",
  "responsibility",
  "result",
  "results",
  "role",
  "scalable",
  "skills",
  "solution",
  "solutions",
  "specialist",
  "spring",
  "sql",
  "stakeholders",
  "strategic",
  "success",
  "summary",
  "support",
  "system",
  "systems",
  "team",
  "teams",
  "technology",
  "technologies",
  "testing",
  "typescript",
  "ui",
  "ux",
  "validation",
  "visualization",
  "vue",
  "web3",
  "workflow",
  "workflows",
];

const DOMAIN_ALLOWLIST = new Set(
  [
    "api",
    "apis",
    "antd",
    "app",
    "appheader",
    "bca",
    "css",
    "cv",
    "cvcanvas",
    "doc",
    "docx",
    "docinsights",
    "express",
    "fx",
    "gi",
    "html",
    "hyderabad",
    "javascript",
    "jira",
    "json",
    "lms",
    "madhava",
    "madhav",
    "metamask",
    "amnog",
    "node",
    "nodejs",
    "pdf",
    "pi",
    "prostate",
    "quill",
    "razorpay",
    "react",
    "reactjs",
    "redux",
    "recharts",
    "resumiq",
    "tatikonda",
    "typescript",
    "upi",
    "visualcv",
    "web3",
  ].map((value) => value.toLowerCase()),
);

const ACTION_VERB_REGEX =
  /\b(developed|implemented|built|worked|designed|led|managed|created|optimized|collaborated|spearheaded|integrated|delivered|supported|handled|provided|focused|maintained|owned|directed|executed|analyzed|documented|improved|conducted)\b/i;

const COMPANY_SUFFIX_REGEX =
  /\b(pvt\.?\s*ltd|private limited|ltd\.?|inc\.?|llc\.?|consulting|solutions|technologies|systems|services|company|corporation|group|labs?|studio|agency|enterprises?|university|college|school|hospital|bank|foundation)\b/i;

export const FIELD_SPELLCHECK_PROPS = {
  spellCheck: true,
  autoCorrect: "on",
  autoCapitalize: "sentences",
};

export function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function tokenize(value = "") {
  return String(value).match(/[A-Za-z][A-Za-z'.-]{2,}/g) || [];
}

function normalizeToken(token = "") {
  return token.toLowerCase().replace(/^[^a-z]+|[^a-z]+$/g, "");
}

function dedupeByKey(items, getKey) {
  const seen = new Set();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function levenshtein(left = "", right = "") {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const matrix = Array.from({ length: left.length + 1 }, () =>
    new Array(right.length + 1).fill(0),
  );

  for (let row = 0; row <= left.length; row += 1) {
    matrix[row][0] = row;
  }

  for (let col = 0; col <= right.length; col += 1) {
    matrix[0][col] = col;
  }

  for (let row = 1; row <= left.length; row += 1) {
    for (let col = 1; col <= right.length; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost,
      );
    }
  }

  return matrix[left.length][right.length];
}

function matchTokenCase(source = "", replacement = "") {
  if (!source) return replacement;
  if (source === source.toUpperCase()) return replacement.toUpperCase();
  if (source[0] === source[0].toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement.toLowerCase();
}

function escapeRegExp(value = "") {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function replaceWholeWord(value, fromWord, toWord) {
  if (!hasText(value) || !hasText(fromWord) || !hasText(toWord)) return value;

  const pattern = new RegExp(`\\b${escapeRegExp(fromWord)}\\b`, "gi");
  return value.replace(pattern, (match) => matchTokenCase(match, toWord));
}

function shouldIgnoreToken(token, allowlist = []) {
  const normalized = normalizeToken(token);

  if (!normalized || normalized.length < 4) return true;
  if (DOMAIN_ALLOWLIST.has(normalized)) return true;
  if (Array.isArray(allowlist) && allowlist.some((item) => normalizeToken(item) === normalized)) {
    return true;
  }
  if (/^(https?|www|com|org|net)$/.test(normalized)) return true;
  if (/^\d/.test(normalized)) return true;
  return false;
}

function findVocabularySuggestion(word) {
  if (!word) return null;

  const normalizedVocabulary = RESUME_VOCABULARY.map((candidate) =>
    normalizeToken(candidate.replace(/\./g, "")),
  );

  if (normalizedVocabulary.includes(word)) {
    return null;
  }

  let bestSuggestion = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of RESUME_VOCABULARY) {
    if (candidate[0] !== word[0]) continue;

    const distance = levenshtein(word, candidate.replace(/\./g, ""));
    if (distance > 2 || distance >= bestDistance) continue;

    bestDistance = distance;
    bestSuggestion = candidate;
  }

  if (!bestSuggestion) return null;

  return {
    suggestion: bestSuggestion,
    autoApply: bestDistance <= 1,
  };
}

export function getTextSuggestions(value, options = {}) {
  const { limit = 3, allowlist = [] } = options;
  const text = stripHtml(value);

  if (!text) return [];

  const suggestions = tokenize(text)
    .map((word) => {
      const normalizedWord = normalizeToken(word);

      if (shouldIgnoreToken(word, allowlist)) return null;

      if (SPELLING_OVERRIDES[normalizedWord]) {
        if (SPELLING_OVERRIDES[normalizedWord] === normalizedWord) {
          return null;
        }

        return {
          word,
          normalizedWord,
          suggestion: SPELLING_OVERRIDES[normalizedWord],
          reason: "Possible spelling correction",
          autoApply: true,
        };
      }

      if (word !== word.toLowerCase()) return null;

      const vocabularyMatch = findVocabularySuggestion(normalizedWord);
      if (!vocabularyMatch) return null;
      if (normalizeToken(vocabularyMatch.suggestion.replace(/\./g, "")) === normalizedWord) {
        return null;
      }

      return {
        word,
        normalizedWord,
        suggestion: vocabularyMatch.suggestion,
        reason: "Suggested correction",
        autoApply: vocabularyMatch.autoApply,
      };
    })
    .filter(Boolean);

  return dedupeByKey(suggestions, (item) => item.normalizedWord).slice(0, limit);
}

export function applySuggestionToText(value, issue) {
  if (!issue?.word || !issue?.suggestion) return value;
  return replaceWholeWord(String(value || ""), issue.word, issue.suggestion);
}

export function autoCorrectText(value, options = {}) {
  let correctedValue = String(value || "");
  const corrections = [];
  const suggestions = getTextSuggestions(correctedValue, {
    ...options,
    limit: 20,
  });

  suggestions.forEach((issue) => {
    if (!issue.autoApply) return;
    const nextValue = applySuggestionToText(correctedValue, issue);

    if (nextValue !== correctedValue) {
      corrections.push(issue);
      correctedValue = nextValue;
    }
  });

  return {
    correctedValue,
    corrections,
  };
}

function looksLikeCompanyPrefix(value) {
  const normalized = stripHtml(value);
  if (!normalized) return false;
  if (normalized.split(/\s+/).length > 10) return false;
  if (!/[A-Z]/.test(normalized)) return false;
  return COMPANY_SUFFIX_REGEX.test(normalized) || /^[A-Z][A-Za-z0-9&().,\-/ ]+$/.test(normalized);
}

export function sanitizeCompanyName(value) {
  const originalValue = stripHtml(value);
  if (!originalValue) {
    return {
      isMalformed: false,
      cleanedValue: "",
      reason: "",
    };
  }

  let cleanedValue = originalValue.replace(/\s+/g, " ").trim();
  const verbMatch = cleanedValue.match(ACTION_VERB_REGEX);

  if (verbMatch && typeof verbMatch.index === "number") {
    const prefix = cleanedValue.slice(0, verbMatch.index).trim();
    if (looksLikeCompanyPrefix(prefix)) {
      cleanedValue = prefix;
    }
  }

  const suffixMatch = cleanedValue.match(
    /^(.*?\b(?:pvt\.?\s*ltd|private limited|ltd\.?|inc\.?|llc\.?|consulting|solutions|technologies|systems|services|company|corporation|group|labs?|studio|agency|enterprises?)\b)\s*[-,:]?\s+(?:developed|implemented|built|worked|designed|led|managed|created|optimized|collaborated|spearheaded|integrated|delivered|supported|handled|provided|focused|maintained|owned|directed|executed|analyzed|documented|improved|conducted)\b/i,
  );

  if (suffixMatch && suffixMatch[1]) {
    cleanedValue = suffixMatch[1].trim();
  }

  cleanedValue = cleanedValue.replace(/[,:;.\-–—]+$/g, "").trim();

  return {
    isMalformed: cleanedValue !== originalValue,
    cleanedValue,
    reason:
      cleanedValue !== originalValue
        ? "Company name looks mixed with responsibilities or achievements."
        : "",
  };
}

export function getCompanyNameIssue(companyName) {
  const validation = sanitizeCompanyName(companyName);
  if (!validation.isMalformed) return null;

  return {
    currentValue: stripHtml(companyName),
    suggestion: validation.cleanedValue,
    reason: validation.reason,
  };
}

function normalizeComparisonText(value) {
  return stripHtml(value).toLowerCase().replace(/\s+/g, " ").trim();
}

export function isProjectNameHighlighted(projectName, descriptionHtml) {
  const project = stripHtml(projectName);
  if (!project) return true;

  const description = String(descriptionHtml || "");
  const escapedProject = escapeRegExp(project)
    .replace(/\s+/g, "\\s+")
    .replace(/\\ /g, "\\s+");

  const highlightedRegex = new RegExp(
    `<(strong|b)[^>]*>\\s*(?:project\\s*:\\s*)?${escapedProject}\\s*<\\/(strong|b)>`,
    "i",
  );
  const labeledRegex = new RegExp(
    `<(strong|b)[^>]*>\\s*project\\s*:?\\s*<\\/(strong|b)>\\s*${escapedProject}`,
    "i",
  );

  return highlightedRegex.test(description) || labeledRegex.test(description);
}

export function prependHighlightedProject(projectName, descriptionHtml = "") {
  const project = stripHtml(projectName);
  if (!project) return descriptionHtml || "";
  if (isProjectNameHighlighted(project, descriptionHtml)) return descriptionHtml || "";

  const existingBody = String(descriptionHtml || "").trim();
  const projectMarkup = `<p><strong>${escapeHtml(project)}</strong></p>`;

  return existingBody ? `${projectMarkup}${existingBody}` : projectMarkup;
}

export function prependProjectSection(projectName, descriptionHtml = "") {
  const project = stripHtml(projectName);
  if (!project) return descriptionHtml || "";

  const existingBody = String(descriptionHtml || "").trim();
  const existingText = normalizeComparisonText(existingBody);
  const labelText = normalizeComparisonText(`Project: ${project}`);

  if (existingText.includes(labelText)) {
    return existingBody;
  }

  const projectMarkup = `<p><strong>Project:</strong> ${escapeHtml(project)}</p>`;
  return existingBody ? `${projectMarkup}${existingBody}` : projectMarkup;
}

export function getProjectHighlightIssue(experienceItem) {
  const projectName = stripHtml(experienceItem?.project || "");
  if (!projectName) return null;

  if (
    experienceItem?.projectDisplayMode === "highlighted" ||
    experienceItem?.projectDisplayMode === "section" ||
    experienceItem?.projectDisplayMode === "separate"
  ) {
    return null;
  }

  if (isProjectNameHighlighted(projectName, experienceItem?.description || "")) {
    return null;
  }

  return {
    message:
      "Do you want to highlight project names or move them to a separate section?",
    projectName,
  };
}

function mapTextWithCorrections(value, label, options = {}) {
  const { correctedValue, corrections } = autoCorrectText(value, options);

  return {
    value: correctedValue,
    corrections: corrections.map((issue) => ({
      ...issue,
      field: label,
    })),
  };
}

export function buildResumeQualitySnapshot(resume) {
  const sourceResume =
    resume && typeof resume === "object"
      ? JSON.parse(JSON.stringify(resume))
      : {};

  const correctedResume = {
    ...sourceResume,
    skillGroups: {
      ...(sourceResume.skillGroups || {}),
      skills: Array.isArray(sourceResume?.skillGroups?.skills)
        ? [...sourceResume.skillGroups.skills]
        : [],
    },
    experience: Array.isArray(sourceResume?.experience)
      ? sourceResume.experience.map((item) => ({ ...item }))
      : [],
    education: Array.isArray(sourceResume?.education)
      ? sourceResume.education.map((item) => ({ ...item }))
      : [],
    languages: Array.isArray(sourceResume?.languages)
      ? [...sourceResume.languages]
      : [],
  };

  const appliedCorrections = [];
  const companyIssues = [];
  const projectIssues = [];

  const topLevelMappings = [
    ["roleTitle", "Role / Title"],
    ["role", "Role"],
    ["summary", "About Me"],
    ["objective", "Career Objective"],
    ["achievements", "Achievements"],
  ];

  topLevelMappings.forEach(([field, label]) => {
    if (!hasText(correctedResume?.[field])) return;
    const result = mapTextWithCorrections(correctedResume[field], label);
    correctedResume[field] = result.value;
    appliedCorrections.push(...result.corrections);
  });

  correctedResume.skillGroups.skills = correctedResume.skillGroups.skills.map(
    (skill, index) => {
      const result = mapTextWithCorrections(skill, `Skill ${index + 1}`);
      appliedCorrections.push(...result.corrections);
      return result.value;
    },
  );

  correctedResume.education = correctedResume.education.map((item, index) => {
    const nextItem = { ...item };

    ["degree", "field"].forEach((field) => {
      if (!hasText(nextItem?.[field])) return;
      const label = `Education ${index + 1} ${field === "degree" ? "Degree" : "Field"}`;
      const result = mapTextWithCorrections(nextItem[field], label);
      nextItem[field] = result.value;
      appliedCorrections.push(...result.corrections);
    });

    return nextItem;
  });

  correctedResume.experience = correctedResume.experience.map((item, index) => {
    const nextItem = { ...item };

    ["role", "project", "description"].forEach((field) => {
      const rawValue =
        field === "description" ? stripHtml(nextItem?.[field] || "") : nextItem?.[field];
      if (!hasText(rawValue)) return;

      const labelMap = {
        role: "Role",
        project: "Project",
        description: "Description",
      };
      const result = mapTextWithCorrections(nextItem[field], `Experience ${index + 1} ${labelMap[field]}`, {
        allowlist: [nextItem.company, nextItem.project],
      });
      nextItem[field] = result.value;
      appliedCorrections.push(...result.corrections);
    });

    const companyIssue = getCompanyNameIssue(nextItem.company);
    if (companyIssue) {
      companyIssues.push({
        index,
        ...companyIssue,
      });
      nextItem.company = companyIssue.suggestion;
    }

    const projectIssue = getProjectHighlightIssue(nextItem);
    if (projectIssue) {
      projectIssues.push({
        index,
        ...projectIssue,
      });
    }

    return nextItem;
  });

  return {
    correctedResume,
    appliedCorrections,
    companyIssues,
    projectIssues,
  };
}

export function useFieldSuggestions(value, options = {}) {
  const memoValue = useMemo(() => stripHtml(value), [value]);
  const [suggestions, setSuggestions] = useState(() =>
    getTextSuggestions(memoValue, options),
  );

  useEffect(() => {
    setSuggestions(getTextSuggestions(memoValue, options));
  }, [memoValue, options]);

  return suggestions;
}
